/**
 * CollapsibleCheck — one row of the dispatch "派送数量核对" checklist (#1676).
 *
 * Every check on the day gets the same shape: a title, a status badge that is
 * readable while collapsed, and a body that only unfolds when it is worth the
 * operator's screen. A passing check keeps its row (so nobody has to wonder
 * whether it ran) without spending vertical space on "✓ 通过" bodies.
 *
 * `open` is seeded from `defaultOpen` on mount and then owned by the operator —
 * a background refetch re-renders this component but must never re-open a row
 * they just folded away. The body mounts on first open and stays mounted after
 * that, so a folded row issues no requests of its own (#2507) and reopening
 * does not refetch.
 */
import { useState, type ReactNode } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@resource-kit/ui/react';

interface CollapsibleCheckProps {
  /** Stable hook for tests. */
  testId: string;
  title: string;
  /** Status shown on the title row — the conclusion survives collapsing. */
  badge: ReactNode;
  defaultOpen: boolean;
  children: ReactNode;
  /** Static one-line "what is compared" text; only the `row` variant shows it. */
  description?: ReactNode;
  /** `row` = one side row inside a grouping card (#2484); `boxed` = standalone. */
  variant?: 'boxed' | 'row';
}

export function CollapsibleCheck({
  testId,
  title,
  badge,
  defaultOpen,
  children,
  description,
  variant = 'boxed',
}: CollapsibleCheckProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [everOpened, setEverOpened] = useState(defaultOpen);
  const onToggle = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) setEverOpened(true);
  };
  const body = everOpened ? children : null;
  if (variant === 'row') {
    return (
      <details
        data-testid={testId}
        open={open}
        onToggle={(event) => onToggle(event.currentTarget.open)}
        className="border-t border-border/60 px-4 py-2.5"
      >
        <summary className="grid cursor-pointer list-none grid-cols-[120px_minmax(0,1fr)_auto] items-center gap-3.5 max-md:grid-cols-[minmax(0,1fr)_auto]">
          <span className="text-sm font-semibold">{title}</span>
          <span className="text-[13px] text-muted-foreground max-md:order-3 max-md:col-span-2">
            {description}
          </span>
          {badge}
        </summary>
        <div className="mt-2 space-y-2">{body}</div>
      </details>
    );
  }
  return (
    <details
      data-testid={testId}
      open={open}
      onToggle={(event) => onToggle(event.currentTarget.open)}
      className="group rounded-md border px-3 py-2 [&[open]]:py-3"
    >
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-semibold">
          <span
            aria-hidden
            className="text-muted-foreground transition-transform group-open:rotate-90"
          >
            ▸
          </span>
          {title}
        </span>
        {badge}
      </summary>
      <div className="mt-2 space-y-2">{body}</div>
    </details>
  );
}

interface ReconcileBadgeProps {
  /** False = the operator has not run the reconcile action for this date yet. */
  triggered: boolean;
  isFetching: boolean;
  isError: boolean;
  /** Rows that disagree with the truth source; 0 = the two sides matched. */
  alertCount: number;
  /** What one disagreeing row is called on this side (drift / diff). */
  alertLabel: string;
  /** When set, a clean result renders this success pill instead of the tick. */
  okLabel?: string;
}

/**
 * The status badge shared by the two reconcile rows (#1676). "Not run yet" is
 * a first-class state here: an unrun check that rendered nothing at all used
 * to read as "nothing to reconcile", which is the opposite of the truth.
 */
export function ReconcileBadge({
  triggered,
  isFetching,
  isError,
  alertCount,
  alertLabel,
  okLabel,
}: ReconcileBadgeProps) {
  const { t } = useTranslation();
  if (isFetching) {
    return (
      <span className="text-sm text-muted-foreground">{t('dispatch.reconcileAll.running')}</span>
    );
  }
  if (!triggered) {
    return <Badge variant="secondary">{t('dispatch.reconcileCheck.notRun')}</Badge>;
  }
  if (isError) {
    return <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />;
  }
  if (alertCount > 0) {
    return (
      <Badge variant="destructive">
        {t('dispatch.reconcileCheck.alertCount', {
          count: alertCount,
          label: alertLabel,
        })}
      </Badge>
    );
  }
  if (okLabel) return <Badge variant="success">{okLabel}</Badge>;
  return (
    <span className="flex items-center gap-1 text-sm text-foreground">
      <Check className="h-4 w-4 text-success" aria-hidden="true" />
      {t('dispatch.salesBridge.passed')}
    </span>
  );
}
